import React, { useEffect, useState } from 'react';
import { AlertCircle, CreditCard } from 'lucide-react';
import { useToast } from '../../../../../hooks/useToast';
import { usePaymentMethods } from '../../../../../hooks/usePaymentMethods';
import { BankAccountRecord, listBankAccounts } from '../../../../../services/BankAccountsService';
import { ChartAccount, listChartOfAccounts } from '../../../../../services/ChartOfAccountsService';
import {
  BillingClient,
  InvoiceSummary,
  OPEN_INVOICE_STATUSES,
  collectInvoice,
  listBillingClients,
  listInvoices
} from '../../../../../services/InvoicesService';
import { JournalLine, postJournalEntry } from '../../../../../services/JournalEntriesService';
import { formatEuro } from '../masters/financeMasters';
import { FormSkeleton, JournalPreview, SearchSelect, formatDate, today } from './FormParts';
import '../../shared-modules.css';
import './FinanceForms.css';

const CASH = 'caja';

interface CollectionFormProps {
  privacyMode: boolean;
  onDone?: () => void;
}

export const CollectionForm = ({ privacyMode, onDone }: CollectionFormProps) => {
  const { notify } = useToast();

  const { methods } = usePaymentMethods();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [clients, setClients] = useState<BillingClient[]>([]);
  const [openInvoices, setOpenInvoices] = useState<InvoiceSummary[]>([]);
  const [banks, setBanks] = useState<BankAccountRecord[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);

  const [clientId, setClientId] = useState('');
  const [date, setDate] = useState(today());
  const [methodId, setMethodId] = useState('MET-TRF');
  // 'caja' o el id de la cuenta bancaria
  const [target, setTarget] = useState('');
  const [reference, setReference] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [clientList, invoices, bankList, chart] = await Promise.all([
          listBillingClients(),
          listInvoices(),
          listBankAccounts(),
          listChartOfAccounts()
        ]);
        if (cancelled) return;
        setClients(clientList);
        setOpenInvoices(invoices.filter(i => OPEN_INVOICE_STATUSES.includes(i.status) && i.total > 0));
        setBanks(bankList);
        setAccounts(chart);
        setTarget(bankList.length > 0 ? bankList[0].id : CASH);
        setLoadError('');
      } catch (err) {
        console.error('Error loading collection form data:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar los datos de cobro.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const pendingOf = (id: string) => openInvoices.filter(i => i.clientId === id);
  const balanceOf = (id: string) => pendingOf(id).reduce((s, i) => s + i.total, 0);

  const clientInvoices = pendingOf(clientId);
  const selected = clientInvoices.filter(i => selectedIds.includes(i.id));
  const amount = selected.reduce((s, i) => s + i.total, 0);

  const bank = banks.find(b => b.id === target);
  const debitAccount = target === CASH
    ? { account: '570000', accountName: 'Caja, euros' }
    : { account: '572000', accountName: bank ? `Bancos c/c (${bank.name})` : 'Bancos c/c' };

  const journalFor = (total: number): JournalLine[] => [
    { ...debitAccount, debit: total, credit: 0 },
    { account: '430000', accountName: 'Clientes (Euros)', debit: 0, credit: total }
  ];

  const handleClientChange = (id: string) => {
    setClientId(id);
    setSelectedIds(pendingOf(id).map(i => i.id));
  };

  // La forma de pago propone la cuenta destino
  const handleMethodChange = (id: string) => {
    setMethodId(id);
    const method = methods.find(m => m.id === id);
    if (!method) return;
    if (method.account === '570000') setTarget(CASH);
    else if (target === CASH && banks.length > 0) setTarget(banks[0].id);
  };

  const toggleInvoice = (id: string) =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const validate = () => {
    if (!clientId) return 'Seleccione un cliente con saldo pendiente.';
    if (!date) return 'La fecha del cobro es obligatoria.';
    if (!target) return 'Seleccione la cuenta destino.';
    if (selected.length === 0) return 'Marque al menos una factura pendiente.';
    return '';
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSaving(true);

    // De una en una. invoices_collect ya contabiliza el cobro; sin cuenta bancaria para no
    // crear un movimiento que no viene del banco
    const done: InvoiceSummary[] = [];
    let failure = '';
    for (const inv of selected) {
      try {
        await collectInvoice(inv.id, { reference: reference.trim(), bankAccountId: '' });
        done.push(inv);
      } catch (err) {
        console.error('Error collecting invoice:', err);
        failure = `No se pudo cobrar ${inv.number}: ${err instanceof Error ? err.message : 'error del servidor.'}`;
        break;
      }
    }

    if (done.length === 0) {
      setFormError(failure);
      setSaving(false);
      return;
    }

    const doneIds = done.map(i => i.id);
    const doneNumbers = done.map(i => i.number).join(', ');
    const doneAmount = done.reduce((s, i) => s + i.total, 0);
    const client = clients.find(c => c.id === clientId);

    // El backend lo apunta en la 572: si es en caja, se pasa a la 570
    let journalError = '';
    if (target === CASH) {
      try {
        const lines: JournalLine[] = [
          { account: '570000', accountName: 'Caja, euros', debit: doneAmount, credit: 0 },
          { account: '572000', accountName: 'Bancos c/c', debit: 0, credit: doneAmount }
        ];
        await postJournalEntry(`Cobro en caja de ${client ? client.name : 'cliente'}: ${doneNumbers}`, lines, 'Cobros', accounts, date);
      } catch (err) {
        console.error('Error posting cash collection journal entry:', err);
        journalError = err instanceof Error ? err.message : 'error del servidor.';
      }
    }

    if (failure) {
      setFormError(`Cobradas: ${doneNumbers}${journalError ? ` (quedan en bancos, no en caja: ${journalError})` : ''}. ${failure}`);
      setOpenInvoices(prev => prev.filter(i => !doneIds.includes(i.id)));
      setSelectedIds(prev => prev.filter(id => !doneIds.includes(id)));
      setSaving(false);
      return;
    }

    if (journalError) {
      notify('Cobro apuntado en bancos', `Facturas cobradas (${doneNumbers}), pero no se pudo pasar el importe a caja: ${journalError}`, 'warning');
    } else {
      notify('Cobro registrado', `${formatEuro(doneAmount, privacyMode)} cobrados (${doneNumbers}) y contabilizados en el Libro Diario [${debitAccount.account.slice(0, 3)} a 430].`, 'info');
    }
    setSaving(false);
    if (onDone) onDone();
  };

  if (loading) return <FormSkeleton />;

  if (loadError) {
    return (
      <div className="module-panel">
        <div className="module-panel-empty">{loadError}</div>
      </div>
    );
  }

  const clientOptions = clients
    .filter(c => pendingOf(c.id).length > 0)
    .map(c => ({ value: c.id, label: c.name, sub: `Saldo vivo: ${formatEuro(balanceOf(c.id), privacyMode)}` }));

  return (
    <div className="fin-doc">
      <div className="fin-doc__header">
        <div>
          <span className="fin-doc__tag">VENTAS Y COBROS • COBROS</span>
          <h3 className="fin-doc__title"><CreditCard size={14} /> Cobro de cliente</h3>
        </div>
        <span className="fin-doc__badge">{target === CASH ? '570000 CAJA' : '572000 BANCOS'}</span>
      </div>

      {formError && (
        <div className="module-form-error">
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          <span>{formError}</span>
        </div>
      )}

      <div className="fin-doc__grid">
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cliente con saldo vivo *</label>
          <SearchSelect
            options={clientOptions}
            value={clientId}
            onChange={handleClientChange}
            placeholder="Buscar cliente por nombre..."
            emptyText="No hay clientes con facturas pendientes"
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Fecha *</label>
          <input
            type="date"
            required
            className="sidebar-form-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Forma de pago *</label>
          <select className="sidebar-form-input" value={methodId} onChange={(e) => handleMethodChange(e.target.value)}>
            {methods.filter(m => m.isActive || m.id === methodId).map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Cuenta destino *</label>
          <select className="sidebar-form-input" value={target} onChange={(e) => setTarget(e.target.value)}>
            {banks.map(b => (
              <option key={b.id} value={b.id}>572000 · {b.name}</option>
            ))}
            <option value={CASH}>570000 · Caja, euros</option>
          </select>
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Importe</label>
          <input
            type="text"
            readOnly
            className="sidebar-form-input fin-doc__mono fin-doc__readonly"
            value={formatEuro(amount, privacyMode)}
          />
        </div>
        <div className="sidebar-form-group">
          <label className="sidebar-form-label">Referencia</label>
          <input
            type="text"
            className="sidebar-form-input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Ej. JUST-TRF-889102"
          />
        </div>
      </div>

      <div className="fin-lines">
        <div className="fin-lines__header">
          <span>Facturas pendientes del cliente</span>
        </div>

        {!clientId ? (
          <div className="fin-lines__empty">Seleccione un cliente para ver sus facturas pendientes.</div>
        ) : (
          <>
            <div className="fin-lines__row fin-lines__row--head fin-lines__row--collect">
              <span />
              <span>Factura</span>
              <span>Emisión</span>
              <span>Vencimiento</span>
              <span className="right">Pendiente</span>
              <span className="right">Importe aplicado</span>
            </div>
            {clientInvoices.map(inv => {
              const isSelected = selectedIds.includes(inv.id);
              return (
                <div
                  key={inv.id}
                  className={`fin-lines__row fin-lines__row--collect ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => toggleInvoice(inv.id)}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleInvoice(inv.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="fin-doc__mono">{inv.number}{inv.status === 'executive' ? ' (vía ejecutiva)' : ''}</span>
                  <span>{inv.issueDate ? formatDate(inv.issueDate) : '—'}</span>
                  <span>{inv.dueDate ? formatDate(inv.dueDate) : '—'}</span>
                  <span className="fin-lines__amount">{formatEuro(inv.total, privacyMode)}</span>
                  <span className="fin-lines__amount">{formatEuro(isSelected ? inv.total : 0, privacyMode)}</span>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="fin-doc__totals">
        <span>Facturas marcadas: <strong>{selected.length}</strong></span>
        <span className="fin-doc__total">Total cobrado: <strong>{formatEuro(amount, privacyMode)}</strong></span>
      </div>

      <JournalPreview lines={journalFor(amount)} privacyMode={privacyMode} />

      <div className="sidebar-form-group">
        <label className="sidebar-form-label">Notas</label>
        <textarea
          rows={3}
          className="sidebar-form-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Observaciones sobre el cobro..."
        />
      </div>

      <div className="fin-doc__actions">
        <button type="button" className="fin-doc__btn fin-doc__btn--primary" disabled={saving} onClick={handleSubmit}>
          {saving ? 'Contabilizando...' : 'Contabilizar cobro'}
        </button>
      </div>
    </div>
  );
};

export default CollectionForm;
